<template>
  <v-container>
    <v-row v-if="loading" class="mt-10" justify="center">
      <v-skeleton-loader type="card, list-item-two-line, table" width="100%" />
    </v-row>

    <template v-else>
      <v-row class="mt-6">
        <v-col cols="12">
          <class-header
            :class-data="form"
            :theme="theme"
            :members-count="activeMembers.length"
            @back="goBack"
          />
        </v-col>
      </v-row>

      <v-row v-if="pendingMembers.length" dense>
        <v-col cols="12">
          <class-requests-panel
            :requests="pendingMembers"
            :theme="theme"
            :normalized-times="normalizedTimes"
            :class-days="form.weekDays || []"
            @reload="loadData"
          />
        </v-col>
      </v-row>

      <v-row dense class="mt-2">
        <v-col cols="12">
          <v-card rounded="xl" elevation="2" class="class-tabs-card">
            <v-tabs
              v-model="activeTab"
              :slider-color="theme.primary"
              class="class-tabs"
              show-arrows
            >
              <v-tab class="class-tabs__tab">
                <v-icon small left>mdi-view-dashboard-outline</v-icon>
                Visão geral
              </v-tab>
              <v-tab class="class-tabs__tab">
                <v-icon small left>mdi-account-group-outline</v-icon>
                Alunos
              </v-tab>
              <v-tab class="class-tabs__tab">
                <v-icon small left>mdi-clipboard-check-outline</v-icon>
                Chamada
              </v-tab>
              <v-tab class="class-tabs__tab">
                <v-icon small left>mdi-progress-check</v-icon>
                Progresso
              </v-tab>
            </v-tabs>

            <v-divider />

            <v-tabs-items v-model="activeTab">
              <v-tab-item>
                <div class="pa-4 pa-md-6">
                  <class-overview-card
                    :class-data="form"
                    :members="activeMembers"
                    :normalized-times="normalizedTimes"
                    :theme="theme"
                    hide-hero
                    :allow-level-edit="true"
                    :level-options="levelOptions"
                    :level-busy="loadingClassLevelUpdate"
                    members-title="Alunos da turma"
                    members-empty-text="Nenhum aluno ativo vinculado a esta turma no momento."
                    @update-level="updateClassLevel"
                  />
                </div>
              </v-tab-item>

              <v-tab-item>
                <div class="pa-4 pa-md-6">
                  <class-students-tab
                    :members="activeMembers"
                    :theme="theme"
                    :normalized-times="normalizedTimes"
                    :class-days="form.weekDays || []"
                    :level-options="levelOptions"
                    :partner-plans="partnerPlans"
                    @reload="loadData"
                  />
                </div>
              </v-tab-item>

              <v-tab-item>
                <div class="pa-4 pa-md-6">
                  <class-attendance-tab
                    :group-id="$route.params.id"
                    :members="activeMembers"
                    :theme="theme"
                    :group-level="form.level"
                  />
                </div>
              </v-tab-item>

              <v-tab-item>
                <div class="pa-4 pa-md-6">
                  <class-progress-tab
                    :group-id="$route.params.id"
                    :members="activeMembers"
                    :theme="theme"
                  />
                </div>
              </v-tab-item>
            </v-tabs-items>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import ClassHeader from "@/components/classes/ClassHeader.vue";
import ClassOverviewCard from "@/components/classes/ClassOverviewCard.vue";
import ClassRequestsPanel from "@/components/classes/ClassRequestsPanel.vue";
import ClassStudentsTab from "@/components/classes/ClassStudentsTab.vue";
import ClassAttendanceTab from "@/components/classes/ClassAttendanceTab.vue";
import ClassProgressTab from "@/components/classes/ClassProgressTab.vue";
import partnerApi from "@/shared/pitchPartnerApi";
import {
  ENGLISH_LEVELS,
  normalizeClassTimes,
} from "@/shared/classPresentation";
import { classThemes } from "@/shared/classThemes";

export default {
  name: "PartnerClassForm",

  components: {
    ClassHeader,
    ClassOverviewCard,
    ClassRequestsPanel,
    ClassStudentsTab,
    ClassAttendanceTab,
    ClassProgressTab,
  },

  data() {
    return {
      loading: true,
      loadingClassLevelUpdate: false,
      activeTab: 0,
      partnerPlans: [],
      form: {
        teacher: {},
        content: [],
      },
    };
  },

  computed: {
    members() {
      return Array.isArray(this.form.content) ? this.form.content : [];
    },
    activeMembers() {
      return this.members.filter((member) => +member.status === 1);
    },
    pendingMembers() {
      return this.members.filter((member) => +member.status === 0);
    },
    normalizedTimes() {
      return normalizeClassTimes(this.form.weekDays, this.form.hours);
    },
    theme() {
      return classThemes.partner;
    },
    levelOptions() {
      return ENGLISH_LEVELS;
    },
  },

  async created() {
    await Promise.all([this.loadData(), this.loadPartnerPlans()]);
  },

  methods: {
    goBack() {
      this.$router.push({ path: "/connect/professor/turmas" });
    },
    async loadData() {
      this.loading = true;

      try {
        const res = await partnerApi.getOneStudyGroups(this.$route.params.id);
        this.form = res.data;
      } catch (error) {
        this.$root.$emit("display-alert", {
          text: "Nao foi possivel carregar os dados da turma.",
          type: 1,
        });
      } finally {
        this.loading = false;
      }
    },
    async loadPartnerPlans() {
      try {
        const res = await partnerApi.getPartnerPlans();
        this.partnerPlans = res.data?.data || [];
      } catch (error) {
        this.partnerPlans = [];
      }
    },
    async updateClassLevel(payload) {
      const level = typeof payload === "object" ? payload.level : payload;
      const studentIds =
        typeof payload === "object" ? payload.studentIds || [] : [];

      if (+this.form.level === +level && studentIds.length === 0) {
        return;
      }

      this.loadingClassLevelUpdate = true;

      try {
        await partnerApi.updateStudyGroupLevel(this.$route.params.id, {
          level: +level,
          studentIds,
        });

        this.form = {
          ...this.form,
          level: +level,
        };

        this.$root.$emit("display-alert", {
          text: studentIds.length
            ? "Nível da turma e dos alunos selecionados atualizado com sucesso."
            : "Nível da turma atualizado com sucesso.",
          type: 0,
        });

        if (studentIds.length) {
          await this.loadData();
        }
      } catch (error) {
        this.$root.$emit("display-alert", {
          text: "Nao foi possivel atualizar o nivel da turma.",
          type: 1,
        });
      } finally {
        this.loadingClassLevelUpdate = false;
      }
    },
  },
};
</script>

<style scoped>
.class-tabs-card {
  overflow: hidden;
}

.class-tabs__tab {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 700;
}
</style>
